import { ArrowBackIosNew, ArrowForwardIos, ChevronRightRounded, CompareRounded, DeleteRounded, History } from '@mui/icons-material'
import DashboardIcon from '@mui/icons-material/Dashboard'
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight'
import { Box, Button, Chip, CircularProgress, IconButton, Paper, Tab, Tabs, Tooltip, Typography, useTheme } from '@mui/material'
import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import AIloader from '../../assets/img/AIloader'
import ApiClient from '../../backend/ApiClient'
import API_URLS from '../../backend/ApiUrls'
import LocalCache from '../../backend/LocalCache'
import { statusOptions } from '../../definitions/Approval'
import { IClassifiedFeature } from '../../definitions/Classification'
import { IContract } from '../../definitions/Contract'
import { IContractSchema } from '../../definitions/ContractSchema'
import { IUploadedFile, IUploadedResult } from '../../definitions/Upload'
import PADUEngine from '../../engines/PADUEngine'
import Helper from '../../helpers/Helper'
import ContractIngestionManager from '../../managers/ContractsIngestionManager'
import ContractsManager, { ValidationContract } from '../../managers/ContractsManager'
import OrchestratorAIManager from '../../managers/OrchestratorAIManager'
import { useAppSelector } from '../../store/Hooks'
import { changeRoute, overwriteRouteParams } from '../../store/slices/SessionSlice'
import PDFViewer from '../controls/PDFViewer'
import ResizableDivider from '../controls/ResizableDivider'
import ContractChangesModal from '../editor/form_builder/contract_editor/ContractChangesModal'
import ContractDuplicateResolutionModal, { DuplicateFeature } from '../editor/form_builder/contract_editor/ContractDuplicateResolutionModal'
import ContractSubmitModal from '../editor/form_builder/contract_editor/ContractManageModal'
import ContractValidationModal from '../editor/form_builder/contract_editor/ContractValidationModal'
import ContractVersioningModal from '../editor/form_builder/contract_editor/ContractVersioningModal'
import EditorForm from '../editor/form_builder/EditorForm'
import ContractFormHelper from '../editor/form_builder/FormHelper'
import PdfSelectModal from '../editor/PdfSelectModal'
import useCommand from '../hooks/useCommand'
import useInvalidate from '../hooks/useInvalidate'
import useInvalidatePagination from '../hooks/useInvalidatePagination'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import VectorIcon from '../icons/VectorIcon'
import Tile from '../layout/Tile'
import UploadModal from '../new_contract_editor/UploadModal'
import RoutingManager from '../routing/Routing'
import { modals } from '../services/ModalService'
import { snackbar } from '../services/SnackbarService'

function ContractEditor() {
    const theme = useTheme()
    const dispatch = useDispatch()
    const now = new Date()
    const { contractUUID, uploadedFiles } = RoutingManager.getParams<{ contractUUID: string, uploadedFiles: IUploadedFile[] }>(useAppSelector(s => s.session.routeParams))
    const [editBoxWidth, setTileWidth] = useState(700)
    const [selectedTab, setSelectedTab] = useState(uploadedFiles.length > 0 ? uploadedFiles[0].key : 'add-new')
    const [contract, setContract] = useState<IContract>(null)
    const [isContractEmpty, setContractIsEmpty] = useState(false)
    const [loadingIngestion, setLoadingIngestion] = useState(false)
    const [ingestionMode, setIngestionMode] = useState(false)
    const [ingestedFeatures, setIngestedFeatures] = useState<IClassifiedFeature[]>([])
    const [ingestionCompleted, setIngestionCompleted] = useState(false)
    const [schema, setSchema] = useState<IContractSchema>()
    const [groupIndex, setGroupIndex] = useState(0)
    const [AIStatus, setAIStatus] = useState<string>()
    const [versioningModalOpen, setVersioningModalOpen] = useState(false)
    const [submitModalOpen, setSubmitModalOpen] = useState(false)
    const [validationModalOpen, setValidationModalOpen] = useState(false)
    const [changesModalOpen, setChangesModalOpen] = useState(false)
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false)
    const [duplicateFeatures, setDuplicateFeatures] = useState<DuplicateFeature[]>([])
    const [pendingIngestionContract, setPendingIngestionContract] = useState<IContract>(null)
    const [validationErrorList, setValidationErrorList] = useState<ValidationContract>(null)
    const [selectedPdfIds, setSelectedPdfIds] = useState<string[]>([])
    const [pdfSelectModalOpen, setPdfSelectModalOpen] = useState(false)

    const paduCompiled = useMemo(() => {
        if (!contract || !schema) return null
        return PADUEngine.compile(contract, schema)
    }, [contract, schema])

    const invalidateFiles = useInvalidate({ queryKey: buildQueryKey('files') })
    const invalidateContracts = useInvalidate({ queryKey: buildQueryKey('contracts') })
    const invalidateContractPendings = useInvalidatePagination({ queryKey: buildQueryKey(API_URLS.os.getPendingContracts + '/*') })
    const invalidateContractsApproval = useInvalidate({ queryKey: buildQueryKey('entity-approval/*') })
    const invalidateContractsVersion = useInvalidate({ queryKey: buildQueryKey('contract-versions/*') })

    const { status: contractStatus, response: contractData } = useQuery({
        queryKey: buildQueryKey('get-contract', contractUUID),
        queryFn: async () => await ApiClient.getContract(contractUUID)
    })

    useEffect(() => {
        if (contractStatus === 'success' && contractData) {
            setContract(contractData)
            setContractIsEmpty(!contractData.lastUpdate)
        }
    }, [contractStatus, contractData])

    useEffect(() => {
        if (contractData?.lastUpdate) {
            setContractIsEmpty(false)
        }
    }, [contractStatus, contractData])

    const { status: schemaStatus, response: schemaResponse } = useQuery({
        queryKey: buildQueryKey('get-schema', contract?.schemaForeignKey),
        queryFn: async () => contract?.schemaForeignKey ? await ApiClient.getContractSchemaByKey(contract?.schemaForeignKey) : null

    })

    useEffect(() => {
        if (schemaStatus === 'success' && schemaResponse) {
            setSchema(schemaResponse)
        }
    }, [schemaStatus, schemaResponse])

    const uploadedFilesMap = useMemo(() => {
        return new Map(uploadedFiles.map(f => [f.key, f.name]))
    }, [uploadedFiles])

    const changes = useMemo(() => {
        return Helper.compareItem(contractData, contract)
    }
    , [contract])

    const { status: contractEditorOptionsStatus, response: contractEditorOptions } = useQuery({
        queryKey: buildQueryKey('contract-editor-options'),
        queryFn: async () => await ApiClient.getSystemConfigsCollection(),
        cache: true
    })

    const { status, response: uploadedFilesResponse } = useQuery({
        queryKey: buildQueryKey('get-uploaded-file', uploadedFiles?.map(x => x.key)?.join(', ')),
        queryFn: async () => await ApiClient.readUploadedFiles(uploadedFiles?.map(x => x?.key)),
        cache: false
    })

    const { execute: executeDeleteFile, status: statusDeleteFile } = useCommand({
        commandFn: (contractUUID: string) => ApiClient.deleteContractFiles(contractUUID),
        onFinish: result => {
            if (result) {
                LocalCache.setStale('files')
                LocalCache.setStale('contracts')
            }
        }
    })

    const handleDeleteFile = (file: IUploadedFile) => {
        modals.openConfirm({
            title: 'Delete File?',
            children: (
                <Typography>
                    This file will be removed from this contract.
                    If not used elsewhere, it will be permanently deleted.
                </Typography>
            ),
            onConfirm: async () => {
                const result = await ApiClient.deleteSingleFile(file.key, contract.uuid)

                if (result?.success) {
                    invalidateFiles()

                    const updatedFiles = uploadedFiles.filter(f => f.key !== file.key)

                    dispatch(overwriteRouteParams({
                        contractUUID,
                        uploadedFiles: updatedFiles
                    }))

                    if (selectedTab === file.key) {
                        setSelectedTab(updatedFiles[0]?.key ?? 'add-new')
                    }

                    snackbar.show({
                        severity: result.action === 'deleted' ? 'success' : 'info',
                        text: result.action === 'deleted'
                            ? 'File permanently deleted'
                            : 'File removed from this contract (used in other versions)'
                    })
                }
            },
            labels: {
                cancel: 'Cancel',
                confirm: 'Delete'
            },
            colors: {
                cancel: 'primary',
                confirm: 'error'
            }
        })
    }

    const { execute: executeDeleteContract, status: statusDeleteContract } = useCommand({
        commandFn: (updatedContract: IContract) => ApiClient.deleteContract(updatedContract),
        onFinish: result => {
            if (result) {
                LocalCache.setStale('contracts')
                LocalCache.setStale('files')
                invalidateContractPendings()
                invalidateContractsApproval()
                invalidateContractsVersion()
            }
        }
    })

    const { execute: executeSave, status: statusSave } = useCommand({
        commandFn: async (updatedContract: IContract) => {
            return ApiClient.updateContract(updatedContract)
        },

        onFinish: result => {
            if (result.response) {
                LocalCache.setStale('contracts')
                LocalCache.setStale('files')
                LocalCache.setStale('get-contract' + contractUUID)
                invalidateContractPendings()
                invalidateContractsApproval()
                setContract(result.response)
                snackbar.show({ severity: 'success', text: 'The contract has been saved successfully' })
            }
        }
    })

    const selectedFileUris = useMemo(() => {
        if (!uploadedFilesResponse) return []

        return uploadedFilesResponse
            .filter(file => selectedPdfIds.includes(file.key))
            .map(file => file.url)
            .filter(Boolean)
    }, [uploadedFilesResponse, selectedPdfIds])

    const { execute: executeAddVersion, status: statusAddVersion } = useCommand({
        commandFn: ({ contractUUID, includeFiles }: { contractUUID: string, includeFiles: boolean }) =>
            ApiClient.addContractVersion(contractUUID, includeFiles),

        onFinish: result => {
            if (result.response) {
                LocalCache.setStale('contracts')
                LocalCache.setStale('files')
                invalidateContractsVersion()
                invalidateContractsApproval()

                snackbar.show({ severity: 'success', text: 'Contract cloned successfully' })

                dispatch(changeRoute({
                    route: 'contract-editor',
                    params: {
                        contractId: result.response.id,
                        uploadedFiles: result.response.files || []
                    }
                }))
            }
        }
    })

    const { status: approvalStatus, response: approvalData } = useQuery({
        queryKey: buildQueryKey('entity-approval', contract?.uuid),
        queryFn: async () => {
            if (!contract?.uuid) {
                return null
            }
            return await ApiClient.getEntityApproval(contract.uuid)
        },
        cache: true
    })

    const indexSelectedFile = uploadedFiles.findIndex(x => x.key === selectedTab)
    const fileUrl = (Helper.hasVal(uploadedFilesResponse) && uploadedFilesResponse.length > 0) ? uploadedFilesResponse[indexSelectedFile]?.url : ''

    const isInitialLoading = status === 'loading' && contractStatus === 'loading' && contractEditorOptionsStatus === 'loading' && schemaStatus === 'loading'
    const isDataReady = contract && schema && contractEditorOptions
    const isChangingLoading = statusSave === 'loading' && statusDeleteContract === 'loading' && statusDeleteFile === 'loading' && statusAddVersion === 'loading'
    const isAIProcessing = ingestionMode && loadingIngestion
    const isReadOnly = contract?.versioning?.isHead === true || (approvalData?.status === 'approved' || approvalData?.status === 'ready-for-review')

    const onSavedFile = (res: IUploadedResult) => {
        invalidateFiles()
        invalidateContracts()
        invalidateContractsVersion()

        const mergedFiles = [
            ...(uploadedFiles ?? []),
            ...(res.uploadedfiles ?? [])
        ]
        dispatch(overwriteRouteParams({ contractUUID: contract.uuid, uploadedFiles: mergedFiles }))

        const newContract = _.cloneDeep(contract)
        newContract.files = mergedFiles
        setContract(newContract)

        if (res.uploadedfiles.length > 0)
            setSelectedTab(res.uploadedfiles[0].key)
    }

    const handleResize = useCallback((newWidth: number) => {
        setTileWidth(Math.min(Math.max(newWidth, 500), 850))
    }, [setTileWidth])

    const refConteiner = useRef<HTMLDivElement>()
    const conteinerWidth = refConteiner?.current?.clientWidth
    const headerHeight = 60

    const handleIngestionToggle = async () => {
        // await executeIngestion()
        setPdfSelectModalOpen(true)
    }

    const handleRestartIngestion = async () => {
        setPdfSelectModalOpen(true)
    }

    const executeIngestion = async () => {
        const allFileUris: string[] = uploadedFilesResponse?.map(file => file.url).filter(url => !!url) || []
        const urisToProcess = selectedFileUris

        if (allFileUris.length === 0) {
            snackbar.show({ severity: 'error', text: 'No files found to process.' })
            setIngestionMode(false)
            return
        }
        if (urisToProcess.length === 0) {
            snackbar.show({ severity: 'error', text: 'Select at least one file.' })
            setIngestionMode(false)
            return
        }

        try {
            setIngestedFeatures([])
            setLoadingIngestion(true)
            setIngestionMode(true)
            setAIStatus('RUNNING')

            const { aiFeatures, status } = await OrchestratorAIManager.run(
                urisToProcess,
                schema,
                contractEditorOptions
            )

            setAIStatus(status)

            if (status === 'SUCCEEDED') {
                const { contract: updatedContract } = await ContractIngestionManager.runIngestionManager(
                    aiFeatures,
                    contract,
                    contractEditorOptions,
                    schema
                )

                setIngestedFeatures(aiFeatures)

                const allFields = schema.steps.flatMap(step => ContractFormHelper.flatSteps(step))
                // const featuresWithAlternatives = aiFeatures
                //     .map(f => ({
                //         feature: f,
                //         field: allFields.find(field => field.key === f.name)
                //     }))

                const featuresWithAlternatives = aiFeatures
                    .map(f => {
                        const field = allFields.find(field => field.key === f.name)

                        // Only keep if there are alternatives (this depends on your data shape)
                        if (!field || !f.alternatives || f.alternatives.length <= 1) {
                            return null
                        }

                        return {
                            feature: f,
                            field
                        }
                    })
                    .filter(Boolean)

                if (featuresWithAlternatives.length > 0) {
                    setDuplicateFeatures(featuresWithAlternatives)
                    setPendingIngestionContract(updatedContract)
                    setDuplicateModalOpen(true)
                    return
                }
                setContract(updatedContract)
                setIngestionCompleted(true)
            }
        } catch (e) {
            console.error('Ingestion Error:', e)
            snackbar.show({ severity: 'error', text: `Ingestion failed with error: "${e.message}"` })
            setAIStatus('FAILED')
        } finally {
            setLoadingIngestion(false)
            setIngestionCompleted(true)
        }
    }

    const handleManualIngestion = () => {
        setIngestionCompleted(true)
    }

    const handleOpenVersioning = () => {
        setVersioningModalOpen(true)
    }

    const handlePdfSelectionConfirm = async () => {
        setPdfSelectModalOpen(false)
        await executeIngestion()
    }

    const handleDuplicateResolution = useCallback(async (resolvedFeatures: IClassifiedFeature[]) => {
        if (!pendingIngestionContract) return

        const newContract = _.cloneDeep(pendingIngestionContract)
        const resFeatures = _.cloneDeep(resolvedFeatures)

        const { contract: updatedContract, features: featuresApplied } = await ContractIngestionManager.runIngestionManager(
            resFeatures,
            newContract,
            contractEditorOptions,
            schema
        )

        setContract(updatedContract)
        setIngestedFeatures(featuresApplied)
        setPendingIngestionContract(null)
        setDuplicateFeatures([])
        setDuplicateModalOpen(false)
        setLoadingIngestion(false)
        setIngestionCompleted(true)
        snackbar.show({ severity: 'success', text: 'Duplicate values resolved successfully' })
    }, [pendingIngestionContract, ingestedFeatures])

    const handleDuplicateModalClose = useCallback(() => {
        if (pendingIngestionContract) {
            setContract(pendingIngestionContract)
            setPendingIngestionContract(null)
        }
        setDuplicateFeatures([])
        setDuplicateModalOpen(false)
        setLoadingIngestion(false)
        setIngestionCompleted(true)
    }, [pendingIngestionContract])

    const onClickManage = () => {
        const isValidContract = ContractsManager.validateContractRequiredFields(contract, schema)
        if (isValidContract.isValid === false) {
            setValidationErrorList(isValidContract)
            setValidationModalOpen(true)
        } else {
            setSubmitModalOpen(true)
        }
    }

    const onClickChanges = () => {
        setChangesModalOpen(true)
    }

    const renderStepHeader = () => {
        if (!schema?.steps) return null

        return (
            <div style={{
                backgroundColor: theme.palette.background.smartable,
                padding: theme.spacing(1),
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(1.33),
                position: 'relative',
                width: '100%',
                borderLeft: 'none',
                height: `${headerHeight}px`,
                minHeight: `${headerHeight}px`
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.66) }}>
                    <IconButton
                        onClick={() => setGroupIndex(Math.max(0, groupIndex - 1))}
                        disabled={groupIndex === 0}>
                        <ArrowBackIosNew fontSize='small' />
                    </IconButton>
                    <IconButton
                        onClick={() => setGroupIndex(Math.min(schema.steps.length - 1, groupIndex + 1))}
                        disabled={groupIndex === schema.steps.length - 1}>
                        <ArrowForwardIos fontSize='small' />
                    </IconButton>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: theme.spacing(0.66) }}>
                    <Typography variant='body2' fontWeight='bold'>
                        {schema.steps[groupIndex]?.label}
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1.33), width: '90%' }}>
                        <div style={{
                            flex: 1,
                            height: 4,
                            backgroundColor: theme.palette.divider,
                            borderRadius: 2
                        }}>
                            <div style={{
                                width: `${(groupIndex + 1) / schema.steps.length * 100}%`,
                                height: '100%',
                                backgroundColor: theme.palette.primary.main,
                                transition: 'width 0.3s ease',
                                borderRadius: 'inherit'
                            }}
                            />
                        </div>
                        <Typography variant='caption' color='textSecondary'>
                            ({groupIndex + 1} of {schema.steps.length})
                        </Typography>
                    </div>
                </div>
            </div>
        )
    }

    const renderAIProcessing = () => (
        <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            height='100%'
            bgcolor={theme.palette.background.default}
            p={4}
            gap={2}>
            <AIloader />
            <Typography variant='h6' textAlign='center'>
                Analyzing contract contents… please hold on.
            </Typography>
            {AIStatus && (
                <Typography variant='body2' color='textSecondary' textAlign='center'>
                    AI status: {AIStatus}
                </Typography>
            )}
        </Box>
    )

    const renderIngestionChoice = () => (
        <Box
            display='flex'
            flexDirection='column'
            height='100%'
            sx={{ maxHeight: 'calc(100vh - var(--top-bar-height) - 120px)' }}>
            {renderStepHeader()}
            <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                flexDirection='column'
                flex={1}
                bgcolor='inherit'
                width='100%'
                pt={2}
                pr={3}
                pb={2}
                pl={3}>
                <Box
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    flexDirection='column'
                    height='100%'
                    bgcolor={theme.palette.background.default}
                    padding={5}
                    gap={3}
                    borderRadius={theme.shape.borderRadius}>
                    <Typography variant='h4'>
                        Contract Editor
                    </Typography>
                    <Typography variant='body1'>
                        Choose how you want to ingest the contract&apos;s data.
                        Do it by yourself or let the AI do the work.
                    </Typography>
                    <Box display='flex' gap={2}>
                        <Button
                            onClick={handleIngestionToggle}
                            variant='contained'
                            size='large'
                            sx={{ alignSelf: 'flex-end' }}>
                            <VectorIcon sx={{ color: theme.palette.primary }} />
                            Ingest with AI
                        </Button>
                        <Button variant='outlined' onClick={handleManualIngestion}>
                            Ingest manually
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    )

    const renderEditorForm = () => {
        if (!schema?.steps || !isDataReady) {
            return (
                <Box display='flex' flex={1} justifyContent='center' alignItems='center' height='100%'>
                    <CircularProgress />
                </Box>
            )
        }
        return (
            <Box
                display='flex'
                flexDirection='column'
                height='100%'
                sx={{ maxHeight: 'calc(100vh - var(--top-bar-height) - 120px)' }}>
                <Box
                    flex={1}
                    sx={{
                        overflow: 'auto'

                    }}>
                    <EditorForm
                        paduCompiled={paduCompiled}
                        data={contract}
                        isReadOnly={isReadOnly}
                        setData={updatedContract => {
                            setContract({ ...updatedContract })
                        }}
                        onReRunAI={handleRestartIngestion}
                        onSave={completedContract => executeSave({ ...completedContract, lastUpdate: now.toISOString() })}
                        disableAutoSave={true}
                        onComplete={() => {}}
                        schema={schema.steps}
                        lightStepper
                        ingestionMode={ingestionMode}
                        features={ingestedFeatures}
                        stringOptions={{
                            'settlementConfigs.exclusions.pharmacy': contractEditorOptions?.find(x => x.uuid === 'exclusions')?.values?.map(v => v.name) ?? [],
                            'settlementConfigs.exclusions.supplemental': contractEditorOptions?.find(x => x.uuid === 'exclusions')?.values?.map(v => v.name) ?? [],
                            'eligibilityRules.esrdExclusion': contractEditorOptions?.find(x => x.uuid === 'exclusions')?.values?.map(v => v.name) ?? [],
                            'eligibilityRules.transplantExclusion': contractEditorOptions?.find(x => x.uuid === 'exclusions')?.values?.map(v => v.name) ?? [],
                            'revenueClaimsCalc.includeSupplementalBenefits': contractEditorOptions?.find(x => x.uuid === 'exclusions')?.values?.map(v => v.name) ?? [],
                            'revenueClaimsCalc.includeReinsurance': contractEditorOptions?.find(x => x.uuid === 'exclusions')?.values?.map(v => v.name) ?? []
                        }}
                        scc={{
                            'type': contractEditorOptions?.find(x => x.uuid === 'contract-types').values ?? [],
                            'productType': contractEditorOptions?.find(x => x.uuid === 'product-types').values ?? [],
                            'qualityAdjustment.adjustmentType': contractEditorOptions?.find(x => x.uuid === 'adjustment-types').values ?? [],
                            'businessSegments': contractEditorOptions?.find(x => x.uuid === 'business-segments').values ?? [],
                            'settlementConfigs.type': contractEditorOptions?.find(x => x.uuid === 'settlement-types').values ?? []
                        }}
                    />
                </Box>
            </Box>
        )
    }

    const renderRightPanelContent = () => {
        if (isInitialLoading || !isDataReady) {
            return (
                <Box display='flex' flex={1} justifyContent='center' alignItems='center' height='100%'>
                    <CircularProgress />
                </Box>
            )
        }

        if (isAIProcessing) {
            return renderAIProcessing()
        }

        if (!ingestionCompleted && isContractEmpty) {
            return renderIngestionChoice()
        }

        return renderEditorForm()
    }

    return (
        <Tile.Container id='editor-container'>
            <Tile.Row style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Tile.Column flex={1}>
                    <Tile.Row alignItems='center'>
                        <Typography fontWeight='bold' variant='h5' mr={1}>
                            Contract Editor
                        </Typography>
                        <Chip
                            icon={<DashboardIcon />}
                            label={schema?.name || 'Loading...'}
                            color='default'
                            variant='filled'
                            size='medium'
                            sx={{
                                borderRadius: '999px',
                                fontWeight: 600,
                                ml: 2
                            }}
                        />

                        {approvalStatus === 'loading'
                            ? (
                                    <CircularProgress size={20} sx={{ ml: 2 }} />
                                )
                            : approvalData && (
                                <Tooltip arrow title='Approvals Status'>
                                    <Chip
                                        sx={{
                                            borderRadius: '999px',
                                            fontWeight: 600,
                                            ml: 2
                                        }}
                                        label={(
                                            <Box display='flex' alignItems='center' gap={1}>
                                                <Box
                                                    width={8}
                                                    height={8}
                                                    borderRadius='50%'
                                                    bgcolor={
                                                        approvalData?.status === 'approved'
                                                            ? 'success.main'
                                                            : approvalData?.status === 'ready-for-review'
                                                                ? 'info.main'
                                                                : approvalData?.status === 'rejected'
                                                                    ? 'red'
                                                                    : 'orange'
                                                    }
                                                />
                                                <Typography variant='body2'>
                                                    {statusOptions.find(option => option.value === approvalData?.status)?.label}
                                                </Typography>
                                            </Box>
                                        )}
                                        variant='outlined'
                                        size='medium'
                                    />
                                </Tooltip>
                            )}
                    </Tile.Row>
                    <Box display='flex' flexDirection='row' alignItems='center' marginLeft={1}>
                        <SubdirectoryArrowRightIcon fontSize='small' sx={{ marginTop: -1 }} />
                        <Typography
                            variant='subtitle1'
                            fontWeight={500}
                            sx={{ textDecoration: 'underline', textDecorationThickness: '1px', marginLeft: 0.5 }}>
                            {contract?.name || 'New Contract'}
                        </Typography>
                    </Box>
                </Tile.Column>
                <Tile.Row
                    style={{
                        justifyContent: 'flex-end',
                        alignItems: 'end',
                        gap: theme.spacing(2)
                    }}>
                    <Box display='flex' alignItems='center' gap={0.5}>
                        <Typography>
                            v{contract?.versioning.version}
                        </Typography>
                        <IconButton
                            disabled={isInitialLoading}
                            onClick={handleOpenVersioning}>
                            <History style={{ color: theme.palette.primary.main }} />
                        </IconButton>
                    </Box>
                    <Button
                        variant='outlined'
                        disabled={isInitialLoading || isReadOnly}
                        color='error'
                        startIcon={
                            (statusDeleteFile === 'loading' || statusDeleteContract === 'loading') &&
                            <CircularProgress size={20} color='info' />
                        }
                        onClick={() => modals.openConfirm({
                            title: 'Do you want to cancel the contract?',
                            children: <Typography>Once the contract is canceled, all associated data will be permanently deleted and cannot be recovered. Please ensure that you have saved any important information before proceeding.</Typography>,
                            labels: {
                                cancel: 'Cancel',
                                confirm: 'Delete'
                            },
                            colors: {
                                cancel: 'primary',
                                confirm: 'error'
                            },
                            onCancel: () => null,
                            onConfirm: () => {
                                executeDeleteFile(contract.uuid)
                                executeDeleteContract({ ...contract })
                                dispatch(changeRoute({ route: 'contracts' }))
                            }
                        })}>
                        Delete
                    </Button>
                    <Button
                        color='success'
                        variant='contained'
                        disabled={isInitialLoading}
                        endIcon={<CompareRounded />}
                        onClick={onClickChanges}>
                        Changes
                    </Button>
                    <Button
                        variant='contained'
                        disabled={isInitialLoading}
                        endIcon={<ChevronRightRounded />}
                        onClick={onClickManage}>
                        {approvalData ? 'Manage' : 'Submit'}
                    </Button>
                </Tile.Row>
            </Tile.Row>
            <Box
                ref={refConteiner}
                sx={{
                    display: 'flex',
                    marginTop: theme.spacing(2),
                    height: 'calc(100vh - var(--top-bar-height) - 120px)',
                    overflow: 'hidden'
                }}>
                <Tile.Row style={{ height: '100%' }}>
                    <Tile.Tile flex={3} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box flex='0 0 auto' style={{ height: `${headerHeight}px` }}>
                            <Tabs
                                value={selectedTab ?? 'add-new'}
                                variant='scrollable'
                                scrollButtons='auto'
                                onChange={(_, newTab) => {
                                    setSelectedTab(newTab)
                                }}
                                sx={{
                                    justifyContent: 'flex-start',
                                    width: conteinerWidth ? `calc(${conteinerWidth}px - ${editBoxWidth}px)` : '100%',
                                    height: '100%'
                                }}>
                                {/* {uploadedFiles?.map(x => (
                                    <Tab key={x.key} label={_.truncate(x.name)} value={x.key} sx={{ fontFamily: 'unset' }} />
                                ))} */}
                                {uploadedFiles?.map(x => (
                                    <Tab
                                        key={x.key}
                                        value={x.key}
                                        label={(
                                            <Box display='flex' alignItems='center' gap={1}>
                                                <Typography variant='body2'>
                                                    {_.truncate(x.name, { length: 20 })}
                                                </Typography>
                                                <Tooltip arrow title='Delete File'>
                                                    <IconButton
                                                        color='error'
                                                        size='small'
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            handleDeleteFile(x)
                                                        }}>
                                                        <DeleteRounded />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        )}
                                        sx={{ fontFamily: 'unset' }}
                                    />
                                ))}
                                <Tab label='+ Add new' disabled={isInitialLoading} value='add-new' sx={{ fontFamily: 'unset' }} />
                            </Tabs>
                        </Box>
                        <Box flex={1} style={{ overflow: 'hidden', minHeight: 0 }}>
                            {selectedTab === 'add-new'
                                ? (
                                        <Box sx={{ height: '100%' }}>
                                            <UploadModal onFinish={onSavedFile} marginTop={20} contractId={contract?.id} disabled={isReadOnly} contractUUID={contract?.uuid} />
                                        </Box>
                                    )
                                : (
                                        <Box sx={{ height: '100%', overflow: 'auto' }}>
                                            {(Helper.hasVal(fileUrl) && fileUrl?.length > 0) && <PDFViewer file={fileUrl} />}
                                        </Box>
                                    )}
                        </Box>
                    </Tile.Tile>
                    <ResizableDivider onResize={handleResize} />
                    <Tile.Tile style={{ width: editBoxWidth, margin: 0, paddingLeft: 10, height: '100%' }}>
                        <Paper
                            elevation={4}
                            sx={{
                                border: 'none',
                                borderRadius: 0,
                                height: '100%',
                                width: editBoxWidth,
                                position: 'relative',
                                pointerEvents: isChangingLoading ? 'none' : undefined,
                                opacity: isChangingLoading ? 0.5 : 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden'
                            }}>

                            {isChangingLoading && (
                                <Box
                                    display='flex'
                                    flex={1}
                                    justifyContent='center'
                                    zIndex={999}
                                    alignItems='center'
                                    height={50}
                                    width={50}
                                    position='absolute'
                                    top='50%'
                                    left='50%'
                                    sx={{ transform: 'translate(-50%, -50%)' }}>
                                    <CircularProgress />
                                </Box>
                            )}

                            {renderRightPanelContent()}
                        </Paper>
                    </Tile.Tile>
                </Tile.Row>
            </Box>

            <ContractVersioningModal
                open={versioningModalOpen}
                onClose={() => setVersioningModalOpen(false)}
                contract={contract}
                addVersionLoading={statusAddVersion === 'loading'}
                onAddVersion={newVersion => executeAddVersion({ contractUUID: newVersion.uuid, includeFiles: false })}
            />

            <ContractSubmitModal
                open={submitModalOpen}
                onAddVersion={newVersion => executeAddVersion({ contractUUID: newVersion.uuid, includeFiles: false })}
                onClose={() => setSubmitModalOpen(false)}
                contract={contract}
                schema={schema}
                onSave={executeSave}
                approval={approvalData}
                saveStatus={statusSave}
                cloneStatus={statusAddVersion}
            />

            <ContractValidationModal
                open={validationModalOpen}
                onClose={() => setValidationModalOpen(false)}
                validationErrorList={validationErrorList}
                onSave={executeSave}
                contract={contract}
                saveStatus={statusSave}
            />
            <ContractChangesModal
                open={changesModalOpen}
                onClose={() => setChangesModalOpen(false)}
                differences={changes}
            />
            <ContractDuplicateResolutionModal
                open={duplicateModalOpen}
                onClose={handleDuplicateModalClose}
                duplicateFeatures={duplicateFeatures}
                onConfirm={handleDuplicateResolution}
            />
            <PdfSelectModal
                open={pdfSelectModalOpen}
                onClose={() => setPdfSelectModalOpen(false)}
                pdfs={uploadedFilesResponse ?? []}
                selected={selectedPdfIds}
                onChange={setSelectedPdfIds}
                onConfirm={handlePdfSelectionConfirm}
                fileNameMap={uploadedFilesMap}
            />
        </Tile.Container>
    )
}

export default ContractEditor
